import { useState, useMemo, useCallback } from "react"
import { useDispatch } from "react-redux"
import { arrayMove } from "@dnd-kit/sortable"
import { columnSorters } from "../equipmentTable/columnSorters"
import { api } from "store/helpers/api"
import { toaster } from "components/common/Toast"
import { getGuideBoatsListing } from "store/boats"

/**
 * Custom hook for managing equipment filtering, sorting, and pagination
 * @param {Array} equipmentData - Raw equipment data
 * @param {Array} categoryListing - Available categories
 * @param {Function} t - Translation function
 * @returns {Object} Filter states, filtered data, and handler functions
 */
export const useEquipmentFilters = (equipmentData, categoryListing, t) => {
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState("")
  const [selectedCategories, setSelectedCategories] = useState([])
  const [sorter, setSorter] = useState({ columnKey: null, order: null })
  // Manual row order: array of row keys in the guide's preferred order.
  // null means "no manual order applied yet — use natural order".
  const [manualOrder, setManualOrder] = useState(null)

  // Derive selected category names (handles 'all' sentinel & id type differences)
  const categoryNames = useMemo(() => {
    if (!Array.isArray(categoryListing) || !categoryListing.length) return []
    if (selectedCategories.includes("all")) return categoryListing.map((cat) => cat.name)
    if (!selectedCategories.length) return []
    const sel = new Set(selectedCategories.map(String))
    return categoryListing.filter((cat) => sel.has(String(cat.id))).map((cat) => cat.name)
  }, [categoryListing, selectedCategories])

  const categoryOptions = useMemo(() => {
    const options = categoryListing.map((cat) => ({
      label: cat?.name,
      value: cat?.id
    }))
    return [{ label: t("All") || "All", value: "all" }, ...options]
  }, [categoryListing, t])

  // Filtered data
  const filteredData = useMemo(() => {
    let data = equipmentData
    const searchLower = search.toLowerCase()

    if (searchLower) {
      data = data.filter((row) => row.title?.toLowerCase().includes(searchLower) || row.category?.toLowerCase().includes(searchLower))
    }

    if (selectedCategories.length && !selectedCategories.includes("all")) {
      const nameSet = new Set(categoryNames.map((n) => n.toLowerCase()))
      data = data.filter((row) => row.category && nameSet.has(row.category.toLowerCase()))
    }

    return data
  }, [equipmentData, search, selectedCategories, categoryNames])

  // Sorted data (column sort)
  const sortedData = useMemo(() => {
    if (!sorter.columnKey || !sorter.order) return filteredData
    const col = columnSorters.find((c) => c.key === sorter.columnKey)
    if (!col?.sorter) return filteredData
    const sorted = [...filteredData].sort(col.sorter)
    if (sorter.order === "descend") {
      sorted.reverse()
    }
    return sorted
  }, [filteredData, sorter])

  // Apply manual drag order on top of the sorted/filtered result.
  // If manualOrder is set, we reorder sortedData to match it (items not in
  // manualOrder fall to the end; items in manualOrder not in sortedData are skipped).
  const orderedData = useMemo(() => {
    if (!manualOrder) return sortedData
    const keyOf = (row) => String(row.key ?? row.id)
    const map = new Map(sortedData.map((row) => [keyOf(row), row]))
    const reordered = manualOrder.filter((key) => map.has(key)).map((key) => map.get(key))
    // Append any rows that weren't in manualOrder (e.g. newly added items)
    const inOrder = new Set(manualOrder)
    sortedData.forEach((row) => {
      if (!inOrder.has(keyOf(row))) reordered.push(row)
    })
    return reordered
  }, [sortedData, manualOrder])

  // Paginated data
  const paginatedData = useMemo(() => {
    return orderedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  }, [orderedData, currentPage, pageSize])

  const handleSearchChange = useCallback((value) => {
    setSearch(value)
    setCurrentPage(1)
    setManualOrder(null)
  }, [])

  const handleSortChange = useCallback((_, __, sorterObj) => {
    setSorter({ columnKey: sorterObj.columnKey, order: sorterObj.order })
    setCurrentPage(1)
    setManualOrder(null)
  }, [])

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page)
  }, [])

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size)
    setCurrentPage(1)
  }, [])

  /**
   * Called when a row is dragged to a new position.
   * activeId and overId are string row keys.
   * We reorder within orderedData (full list) so pagination stays stable.
   */
  const handleReorder = useCallback(
    (activeId, overId) => {
      const keyOf = (row) => String(row.key ?? row.id)
      const currentOrder = orderedData.map(keyOf)
      const oldIndex = currentOrder.indexOf(activeId)
      const newIndex = currentOrder.indexOf(overId)
      if (oldIndex === -1 || newIndex === -1) return
      const newOrder = arrayMove(currentOrder, oldIndex, newIndex)
      setManualOrder(newOrder)
      const pageIds = newOrder
        .slice((currentPage - 1) * pageSize, currentPage * pageSize)
        .map(Number)
      dispatch(
        api({
          url: `/guide/boats/reorder?page=${currentPage}&per_page=${pageSize}`,
          method: "put",
          data: { ids: pageIds }
        })
      ).then(() => {
        toaster.success("Equipment order saved")
        dispatch(getGuideBoatsListing())
      })
    },
    [orderedData, currentPage, pageSize, dispatch]
  )

  // Drag-to-reorder is mutually exclusive with column sorting: a manual order
  // can't be honored while the list is sorted by a column.
  const isSortActive = Boolean(sorter.columnKey && sorter.order)

  // Drag-to-reorder is also disabled while any filter narrows the list, since a
  // saved order only makes sense against the full, unfiltered list.
  const isFilterActive = useMemo(() => {
    const hasSearch = Boolean(search?.trim())
    const hasCategories = selectedCategories.length > 0
    return hasSearch || hasCategories
  }, [search, selectedCategories])

  // Reordering is disabled when either column sorting or any filter is active.
  const dragDisabled = isSortActive || isFilterActive

  return {
    // State
    search,
    selectedCategories,
    currentPage,
    pageSize,
    isSortActive,
    dragDisabled,
    // Derived data
    categoryOptions,
    filteredData,
    paginatedData,
    // Handlers
    setSearch: handleSearchChange,
    setSelectedCategories,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange,
    handleReorder
  }
}
